
import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  SettingsForm,
  SettingsTab,
  normalizeSettingsTab,
} from '@/components/admin/SettingsForm';
import { useSettings } from '@/hooks/useSettings';

const SETTINGS_TABS: SettingsTab[] = [
  'branding',
  'categories',
  'activity',
  'moderation',
  'responses',
  'stage',
  'integration',
  'practice',
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const initialTab = useMemo(() => {
    const tab = searchParams.get('tab');
    const normalized = normalizeSettingsTab(tab);
    return normalized && SETTINGS_TABS.includes(normalized) ? normalized : undefined;
  }, [searchParams]);

  // Admin-only page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/unauthorized', { replace: true });
    }
  }, [user, navigate]);

  const { settings, isLoading, isSaving, error, updateSettings, fetchSettings } = useSettings();

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Header */}
      <PageHeader
        title="Settings"
        description="Configure application settings, categories, and integrations"
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {isLoading ? (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
            <div className="space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-200 dark:bg-slate-700 rounded" />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <SettingsForm
          settings={settings}
          isSaving={isSaving}
          onSave={updateSettings}
          refetchSettings={fetchSettings}
          initialTab={initialTab}
        />
      )}

      {/* Info Section */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InfoIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
              About Settings
            </h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Category Keywords:</strong> Used for automatic prayer
                  categorization based on content analysis.
                </li>
                <li>
                  <strong>Display Settings:</strong> Control how prayers appear during
                  live services.
                </li>
                <li>
                  <strong>Responses:</strong> Enable or disable partner responses
                  and view recording consent records.
                </li>
                <li>
                  <strong>Integrations:</strong> Connect Clearstream for SMS and Mailgun or Resend for transactional email (invite and notification messages). Expand each integration on this page to configure API keys.
                </li>
                <li>
                  <strong>Practice Simulation:</strong> Start and stop practice
                  mode so moderators and producers can practice with simulated
                  submissions.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}
